import { graphql } from "gatsby"
import React, { useContext } from "react"

import Section from "@atoms/Section/Section"
import Layout from "@components/layout.js"
import SEO from "@components/seo"
import { SiteContext } from "@context/Provider"
import i18nWrapper from "@lang/i18nWrapper"
import Breadcrumb from "@molecules/Breadcrumb/Breadcrumb"
import SiteTaxonomyTermPageFeed from "@organisms/SiteTaxonomyTermPageFeed/SiteTaxonomyTermPageFeed"
import Hero from "@organisms/Hero/Hero"
import { useTranslation } from "react-i18next"
import { PRE_LOAD_IMAGE_WIDTHS } from "@helpers/constants"
import { getSiteTermPageFeedData } from "@helpers/templates"
import { formatRelatedArticles } from "@helpers/generic"
import Blocks from "@blocks/Blocks/Blocks"
import SliderOrganism from "@organisms/Slider/Slider"
import SitesList from "@organisms/SitesList/SitesList"
import { roundToNearestHundred, localizeNumber } from "@helpers/generic"
import { SitesListProvider } from "@context/sitesList"

const SiteTypePage = (props) => {
  const {
    data: { single, sites, globalBlocks: wp, relatedPosts },
    uri,
    pageContext: { fallbackImage: groupGlobalImageFallbacks },
  } = props

  const { name, description, seo, typeTermMeta, language } = single

  const {
    themeSettings: { themeSettings: groupGlobalSiteTypeTermPageBlocks },
  } = wp

  const { groupGlobalSiteTypeTermPageBlocks: siteTypeTermPageGlobalBlocks } =
    groupGlobalSiteTypeTermPageBlocks

  const globalBlocks =
    siteTypeTermPageGlobalBlocks?.siteTypeTermPageGlobalBlocks?.map(
      ({ siteTypeTermPageGlobalBlock }) => {
        return siteTypeTermPageGlobalBlock?.blocks[0]
      }
    )

  const { t } = useTranslation()

  const context = useContext(SiteContext)
  context.currentLanguage = language.slug
  context.title = name

  const siteMetaManifest = [
    {
      label: t("Floor area available for rent"),
      key: "fieldEqSiteFieldsRentableFloorArea",
      suffix: "m²",
      filterByAction: [roundToNearestHundred, localizeNumber],
    },
  ]

  // TODO:: Maybe get this from BE
  const ancestors = [
    {
      title: "Kohteemme",
      uri: "/kohteemme",
    },
  ]

  const heroButtons = []
  const componentsData = []

  const sortedOrdered = getSiteTermPageFeedData(
    sites?.nodes,
    groupGlobalImageFallbacks
  )

  sortedOrdered.forEach((val, key) => {
    heroButtons.push({
      link: {
        url: `${uri}#${val.slug}`,
        title: key,
      },
    })
    componentsData.push(val)
  })

  // Preload the first image
  let preLoadImage = null

  if (typeTermMeta?.hero) {
    preLoadImage = typeTermMeta?.hero
  }

  const relatedArticles = formatRelatedArticles(relatedPosts)

  return (
    <Layout currentLanguage={language.slug} lang={language.locale}>
      <SEO
        title={seo?.title ? seo.title : name}
        uri={uri}
        lang={""} // For some reason setting lang as empty in this template fixes alternate links issue, where they always pointed to frontpage url. This fix removes alternate links completely.
        headJson={seo?.headJSON ?? ""}
        currentLanguage={language.slug}
        preLoadImage={preLoadImage}
        preLoadImageWidths={PRE_LOAD_IMAGE_WIDTHS}
      />
      <Section breakOut cappedWidth margins="mb-0">
        <Hero
          title={name}
          image={typeTermMeta.hero}
          mobileImage={typeTermMeta.heroMobile}
          buttons={heroButtons}
        />
      </Section>
      <Section margins={"my-0"}>
        <Breadcrumb ancestors={ancestors} current={name} />
      </Section>
      {description && (
        <Section>
          <div className={"max-w-post mx-auto md:px-12 xl:px-0"}>
            <p
              className={
                "lg:mb-4 lg:mx-0 text-xl lg:text-2xl mx-mobile-gutter lg:mx-auto font-semibold"
              }
            >
              {description}
            </p>
          </div>
        </Section>
      )}
      <SiteTaxonomyTermPageFeed
        componentsData={componentsData}
        langSlug={language.slug}
      />
      <SitesListProvider name={name}>
        <SitesList
          list={sites?.nodes}
          siteMetaManifest={siteMetaManifest}
          type="simple"
        />
      </SitesListProvider>
      <Section>
        <Blocks blocks={globalBlocks} />
      </Section>
      {!!relatedArticles?.length && (
        <Section breakOut margins="my-0">
          <SliderOrganism
            title={`${t("See related articles")}`}
            slides={relatedArticles}
            theme="dark"
            type="large"
          />
        </Section>
      )}
    </Layout>
  )
}

export default i18nWrapper("SiteTypePage")(SiteTypePage)

export const query = graphql`
  query SINGLE_TYPE($id: String!, $name: String!) {
    single: wpTaxonomySiteType(id: { eq: $id }) {
      name
      description
      seo {
        headJSON
        title
      }
      language {
        slug
        locale
      }
      slug
      typeTermMeta {
        hero {
          cloudinaryDetails {
            cloudinary_url
            format
            original_filename
            public_id
          }
          mediaDetails {
            height
            width
          }
          sourceUrl
        }
        heroMobile {
          cloudinaryDetails {
            cloudinary_url
            format
            original_filename
            public_id
          }
          mediaDetails {
            height
            width
          }
          sourceUrl
        }
      }
    }
    sites: allWpPostTypeSite(
      filter: {
        taxonomySiteTypes: { nodes: { elemMatch: { id: { eq: $id } } } }
      }
      sort: { siteMeta: { fieldEqSiteFieldsRentPriority: ASC } }
    ) {
      nodes {
        ...SiteCardData
        taxonomySiteClassifications {
          nodes {
            name
            slug
            description
            classificationTermMeta {
              previewImage {
                cloudinaryDetails {
                  cloudinary_url
                  format
                  original_filename
                  public_id
                }
                mediaDetails {
                  height
                  width
                }
                sourceUrl
              }
            }
          }
        }
        wpChildren {
          nodes {
            ... on WpPostTypeSiteContact {
              id
              uri
            }
          }
        }
      }
    }
    globalBlocks: wp {
      themeSettings {
        themeSettings {
          groupGlobalSiteTypeTermPageBlocks {
            siteTypeTermPageGlobalBlocks {
              siteTypeTermPageGlobalBlock {
                ... on WpPostTypeWpBlock {
                  id
                  blocks {
                    ...BlocksFirstLevel
                    ...Blocks
                  }
                }
              }
            }
          }
        }
      }
    }
    relatedPosts: allWpPost(
      filter: { tags: { nodes: { elemMatch: { name: { eq: $name } } } } }
    ) {
      nodes {
        ...PostCardData
      }
    }
  }
`
